import { createContentfulProps } from "frontend/contentful/lib/create-contentful-props";
import {
  BigGridListEntry,
  RichTextEntry,
  SectionIntroTextEntry,
  SupportingDetailsEntry,
  TruncatorEntry,
} from "frontend/contentful/schema/blocks";
import { TruncatorProps } from "design-system/components/blocks/truncator/truncator";
import { RenderContentfulEntry } from "../utils/render-contentful-entry";
import { isContentType } from "frontend/contentful/lib/is-content-type";
import { useContentful } from "frontend/hooks/use-contentful";

export interface TruncatorOptions {
  headerAbove?: boolean;
}

export const createTruncatorProps = createContentfulProps<
  TruncatorEntry,
  TruncatorProps,
  TruncatorOptions
>(({ entry, options, createEditAttributes }) => {
  const { getEntries } = useContentful();

  const truncatorAlignment = entry.fields.alignment || "wide";
  const headerAbove = options?.headerAbove ?? false;

  const props: TruncatorProps = {
    labelHidden: entry.fields.hiddenStateLabel,
    labelVisible: entry.fields.visibleStateLabel,
    align: truncatorAlignment,
    editAttributes: {
      hiddenStateLabel: createEditAttributes({
        entry,
        fieldId: "hiddenStateLabel",
      }),
      visibleStateLabel: createEditAttributes({
        entry,
        fieldId: "visibleStateLabel",
      }),
    },
    children: getEntries(entry.fields.content)?.map((block, i) => {
      /**
       * Default alignments for truncator blocks
       *
       * BigGridList: full (not configurable via contentful)
       * MediaAssetRow: wide
       * MultipleColumnList: center
       * PodcastPlayer: center
       * PullQuote: none
       * RichText: center (not configurable via contentful)
       * SectionIntroText: full (not configurable via contentful)
       * SmallGridList: wide
       * StatisticsGroup: full
       * StatisticsRow: wide
       * SupportingDetails: full (not configurable via contentful)
       * TeaseFeedArticles: center
       * TeaseFeedBooks: center
       * TeaseRowArticles: wide
       * TeaseRowBooks: wide
       * TeaseRowContents: wide
       * TeaseRowCourse: wide
       * TeaseRowParticipantStories: wide
       * TeaseRowPeople: wide
       * TimelineTease: center
       */

      const isRichTextBlock = isContentType<RichTextEntry>("richText", block);
      const isBigGridListBlock = isContentType<BigGridListEntry>(
        "bigGridList",
        block,
      );
      const isSectionIntroTextBlock = isContentType<SectionIntroTextEntry>(
        "sectionIntroText",
        block,
      );
      const isSupportingDetailsBlock = isContentType<SupportingDetailsEntry>(
        "supportingDetails",
        block,
      );
      const isNonConfigurableFullAlignmentBlock =
        isBigGridListBlock ||
        isSectionIntroTextBlock ||
        isSupportingDetailsBlock;

      if (isNonConfigurableFullAlignmentBlock) {
        return (
          <RenderContentfulEntry
            key={i}
            entry={block}
            options={{
              alignment: truncatorAlignment,
              headerAbove: headerAbove,
            }}
          />
        );
      }

      // Override any blocks that are full or undefined alignment to match truncator alignment
      if (!isRichTextBlock) {
        const hasSetAlignment =
          "alignment" in block.fields &&
          typeof block.fields.alignment !== "undefined";
        const hasFullAlignment =
          hasSetAlignment && block.fields.alignment === "full";
        if (!hasSetAlignment || hasFullAlignment) {
          block.fields.alignment = truncatorAlignment;
        }
        return (
          <RenderContentfulEntry
            key={i}
            entry={block}
            options={{ headerAbove: headerAbove }}
          />
        );
      }

      return (
        <RenderContentfulEntry
          key={i}
          entry={block}
          options={{ headerAbove: headerAbove }}
        />
      );
    }),
  };

  return props;
});
